<script setup>
import { computed, onMounted, onUnmounted, ref, watch } from "vue";
import { getUnixTime, startOfDay, startOfWeek, eachDayOfInterval } from "date-fns";
import { firstDay } from "db";
import { isMobile, sortByKey } from "lib/modules/util";
import { methodIcon } from "lib/booking/modules/util";
import { useResize } from "vui/composables/resize";

const props = defineProps({
	active: {
		type: String,
		default: null
	},
	busy: {
		type: Boolean,
		default: false
	},
	events: {
		type: Array,
		required: true
	},
	start: {
		type: Number,
		required: false
	}
});

const emit = defineEmits(["add", "drop", "open", "load"]);

const dateLabelFormat = new Intl.DateTimeFormat(document.documentElement.lang, { day: "numeric", weekday: "short" });
const dateLabelLongFormat = new Intl.DateTimeFormat(document.documentElement.lang, { year: "numeric", month: "short", day: "numeric", weekday: "long" });
const dateMonthLongFormat = new Intl.DateTimeFormat(document.documentElement.lang, { month: "long" });
const dateMonthShortFormat = new Intl.DateTimeFormat(document.documentElement.lang, { month: "short" });
const dateYearFormat = new Intl.DateTimeFormat(document.documentElement.lang, { year: "numeric" });
const timeFormat = new Intl.DateTimeFormat(document.documentElement.lang, { hour: "numeric", minute: "numeric" });

const now = ref(getUnixTime(Date.now()));
const calendar = ref(null);
const content = ref(null);
const scrollbarWidth = ref(0);
const datePicker = ref(false);

const size = useResize(calendar);
let nowTimer = null;
let drag = null;

const dayCount = computed(() => {
	if (size.width >= 861 + scrollbarWidth.value)
		return 7;
	else if (size.width >= 513 + scrollbarWidth.value)
		return 3;

	return 1;
});

const dayWidth = computed(() => {
	if (size.width >= 63 + 114 + scrollbarWidth.value)
		return Math.floor((size.width - 63 - scrollbarWidth.value) / dayCount.value);

	return 114;
});

const startTime = ref(getStartTime(props.start));
const endTime = computed(() => timeAdd(startTime.value, dayCount.value));

const hourLabels = getHourLabels();
const dayLabels = computed(() => {
	const ds = [];
	const dfmt = dayCount.value > 1 ? dateLabelFormat : dateLabelLongFormat;
	const today = new Date(now.value * 1000).toDateString();

	for (const d of eachDayOfInterval({ start: startTime.value * 1000, end: (endTime.value - 1) * 1000 }))
		ds.push({ today: d.toDateString() === today, label: dfmt.format(d) });

	return ds;
});

const timelinePos = computed(() => {
	if (now.value < startTime.value || now.value >= endTime.value)
		return null;

	const offset = now.value - startTime.value - dstDiff(now.value, startTime.value);
	const day = Math.floor(offset / 86400);
	const sec = offset % 86400;

	return {
		left: day * dayWidth.value + "px",
		top: Math.floor(sec / 60) + "px"
	};
});

const visibleEvents = computed(() => {
	const events = [];

	for (const e of props.events.filter((ev) => ev.start < endTime.value && ev.start + ev.duration > startTime.value)) {

		const key = e.event_id || e.orgevent_id;
		const elements = [];
		let offset = e.start - startTime.value - dstDiff(e.start, startTime.value);
		let first = true;
		let duration = Math.max(1, e.duration - dstDiff(e.start + e.duration, e.start));

		if (offset < 0) {
			duration += offset;
			offset = 0;
			first = false;
		}

		let day = Math.floor(offset / 86400);
		let sec = offset % 86400;

		while (duration > 0 && day < dayCount.value) {
			let dur = duration;

			if (sec + duration > 86400)
				dur = 86400 - sec;

			elements.push({
				left: day * dayWidth.value + 1 + "px",
				top: Math.floor(sec / 60) + 1 + "px",
				height: Math.max(15, Math.ceil(dur / 60)),
				first,
				last: dur === duration,
				lineClamp: Math.max(1, Math.floor(dur / 900) - (first ? 1 : 0))
			});

			sec = 0;
			duration -= dur;
			day++;
			first = false;
		}

		const ext = typeof e.orgevent_id === "undefined";

		events.push({
			...e,
			_key: key,
			_ext: ext,
			_offset: Math.floor(offset / 60),
			_elements: elements,
			_class: e.role || (e.title === null && "private") || (ext && "ext") || (e.orgevent_id === null ? "userevent" : "orgevent"),
			_draggable: !isMobile && e.role !== "guest" && (e.orgevent_id === null || !e.event_id)
		});
	}

	return sortByKey(events, "_key");
});

function dstDiff(ts, tsRef) {
	return (new Date(ts * 1000).getTimezoneOffset() - new Date(tsRef * 1000).getTimezoneOffset()) * 60;
}

function timeAdd(ts, days, minutes = 0) {
	const t = ts + days * 86400 + minutes * 60;

	return t + dstDiff(t, ts);
}

function getStartTime(ts = now.value) {
	return getUnixTime(dayCount.value < 7 ? startOfDay(ts * 1000) : startOfWeek(ts * 1000, { weekStartsOn: firstDay }));
}

function getHourLabels() {
	const hrs = [];
	const ts = new Date("Jan 1, 1970").getTime();

	for (let i = 0; i < 24; i++)
		hrs.push(timeFormat.format(ts + (3600000 * i)));

	return hrs;
}

function updateNow() {
	const t = Date.now();
	now.value = getUnixTime(t);

	const next = 60100 - t % 60000;

	nowTimer = setTimeout(updateNow, next);
}

function stepDay(offset) {
	startTime.value = timeAdd(startTime.value, offset);
}

function addEvent(event) {
	const days = Math.floor(event.offsetX / dayWidth.value);
	const minutes = Math.round(event.offsetY / 15) * 15;
	const start = timeAdd(startTime.value, days, minutes);

	emit("add", start);
}

function addEventNow() {
	const d = new Date();

	d.setHours(d.getHours() + 1, 0, 0, 0);

	emit("add", getUnixTime(d));
}

function dragStart(event, e) {
	drag = {
		x: event.target.offsetLeft + event.offsetX,
		y: event.target.offsetTop + event.offsetY,
		dropped: false
	};

	event.dataTransfer.setData("text/plain", e.title);
	event.dataTransfer.effectAllowed = e.booking_id ? "move" : "copyMove";
}

function drop(event, x = 0, y = 0) {
	if (drag) {
		drag.x = event.offsetX + x - drag.x;
		drag.y = event.offsetY + y - drag.y;
		drag.dropped = true;
	}
}

function dragEnd(event, e) {
	if (drag && drag.dropped) {
		const days = Math.round(drag.x / dayWidth.value);
		const minutes = Math.round(drag.y / 15) * 15;
		const start = timeAdd(e.start, days, minutes);

		if (start !== e.start)
			emit("drop", e, start, event.dataTransfer.dropEffect);
	}

	drag = null;
}

watch(startTime, () => emit("load", startTime.value));

onMounted(() => {
	startTime.value = getStartTime(props.start); // size/dayCount unknown until mounted

	content.value.scroll({ top: 720 + 8 - (content.value.clientHeight >> 1) });

	scrollbarWidth.value = content.value.offsetWidth - content.value.clientWidth;
});

onUnmounted(() => {
	clearTimeout(nowTimer);
});

updateNow();
</script>

<template>
	<div ref="calendar" class="vui-calendar" :style="{ '--day-count': dayCount, '--day-width': dayWidth + 'px' }">
		<div class="header grid align-items-center" :class="{ small: dayCount <= 1 }" :style="{ marginRight: scrollbarWidth + 'px' }">
			<div class="date-container">
				<button :class="{ today: dayCount === 1 && dayLabels[0].today }" class="date vui-btn text justify-self-start" @click="datePicker = true">
					<template v-if="dayCount > 1">
						<span class="month">{{ (size.width <= 600 + scrollbarWidth ? dateMonthShortFormat : dateMonthLongFormat).format(startTime * 1000) }}</span>
						<span>{{ dateYearFormat.format(startTime * 1000) }}</span>
					</template>
					<span v-else class="day">{{ dayLabels[0].label }}</span>
					<i class="fa-regular fa-angle-down"></i>
				</button>
				<template v-if="datePicker">
					<UiDatePicker
						:model-value="startTime"
						class="elevated"
						@update:model-value="startTime = getStartTime($event); datePicker = false"
					/>
					<UiBackdrop class="transparent" @close="datePicker = false"/>
				</template>
			</div>
			<div class="buttons">
				<button type="button" class="vui-btn text" @click="addEventNow()"><i class="fa-regular fa-calendar-plus"></i></button>
				<button type="button" class="vui-btn text" @click="stepDay(-dayCount)"><i class="fa-solid fa-chevron-left"></i></button>
				<button type="button" class="vui-btn text today" @click="startTime = getStartTime()">{{ $gettext("Today") }}</button>
				<button type="button" class="vui-btn text" @click="stepDay(dayCount)"><i class="fa-solid fa-chevron-right"></i></button>
			</div>
		</div>
		<div v-if="dayCount > 1" class="days grid justify-items-center">
			<div v-for="day in dayLabels" class="dow" :class="{ today: day.today }">{{ day.label }}</div>
		</div>
		<div ref="content" class="content grid gap-4">
			<div class="hours grid justify-items-end">
				<div v-for="hour in hourLabels">{{ hour }}</div>
			</div>
			<div
				:key="`${dayCount}:${dayWidth}:${startTime}`"
				class="timegrid"
				@dblclick.self="addEvent"
				@dragover.self.prevent
				@drop.self.prevent="drop"
			>
				<div
					v-for="e in visibleEvents"
					:key="e._key"
					class="event"
				>
					<div
						v-for="(el, idx) in e._elements"
						:key="idx"
						class="element"
						:class="[
							e._class,
							{
								first: el.first,
								last: el.last,
								active: active !== null && active === e.booking_id,
								pending: e.pending,
								short: el.height < 30
							}
						]"
						:style="{ left: el.left, top: el.top, height: el.height - 1 + 'px', paddingTop: el.height < 20 ? '0' : '1px', zIndex: e._offset }"
						:draggable="e._draggable"
						@dragstart.self="dragStart($event, e)"
						@dragend.self="dragEnd($event, e)"
						@dragover.self.prevent
						@drop.self.prevent="drop($event, $event.target.offsetLeft, $event.target.offsetTop)"
						@click="$emit('open', e, $event)"
					>
						<span v-if="el.first" class="time">{{ timeFormat.format(e.start * 1000) }}</span>
						<i v-if="e.method" :class="methodIcon(e.method)" class="icon fa-regular"></i>
						<div class="title" :style="{ '-webkit-line-clamp': el.lineClamp }">{{ e.title === null ? $gettext('Private event') : e.title }}</div>
					</div>
				</div>
			</div>
			<div v-if="timelinePos" class="timeline grid align-items-center gap-4" :style="{ top: timelinePos.top }">
				<div class="time">{{ timeFormat.format(now * 1000) }}</div>
				<div class="line">
					<div class="day-mark" :style="{ left: timelinePos.left }"></div>
				</div>
			</div>
		</div>
		<div v-if="busy" class="loading"><i class="fa-regular fa-spin fa-spinner-third"></i></div>
	</div>
</template>
